import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { Clock, RefreshCw, AlertCircle, CheckCircle, PlayCircle, ArrowUp } from 'lucide-react';

export function WebhookQueue() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastProcessedTime, setLastProcessedTime] = useState<Date | null>(null);
  
  const { data: queueItems, isLoading, refetch } = useQuery({
    queryKey: ['webhook-queue'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_queue')
        .select(`
          *,
          webhook_triggers (
            trigger_id,
            webhook_endpoints (url),
            webhook_event_types (event_code)
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });
  
  const { data: cronStatus, isLoading: isLoadingCron, refetch: refetchCronStatus } = useQuery({
    queryKey: ['cron-job-status'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cron_job_logs')
        .select('*')
        .eq('job_name', 'process-webhook-queue')
        .order('execution_time', { ascending: false })
        .limit(1);
      
      if (error) throw error;
      return data?.[0] || null;
    },
  });
  
  const processQueueMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('process-webhook-queue', {
        body: {}
      });
      
      if (error) throw error;
      return data;
    },
    onMutate: () => {
      setIsProcessing(true);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['webhook-queue'] });
      setLastProcessedTime(new Date());
      toast({
        title: 'Queue Processing Initiated',
        description: `Processed ${data?.processed || 0} webhook(s)`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Processing Failed',
        description: error.message || 'An error occurred while processing the queue',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setIsProcessing(false);
      refetchCronStatus();
    }
  });
  
  useEffect(() => {
    const intervalId = setInterval(() => {
      refetch();
      refetchCronStatus();
    }, 15000);
    
    return () => clearInterval(intervalId);
  }, [refetch, refetchCronStatus, queryClient]);
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case 'processing':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Processing</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Completed</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  const handleProcessQueue = () => {
    processQueueMutation.mutate();
  };
  
  const handleRefresh = () => {
    refetch();
    refetchCronStatus();
    toast({
      title: 'Refreshed',
      description: 'Webhook queue data has been refreshed',
    });
  };
  
  const handleResetFailedWebhooks = async () => {
    try {
      const { error } = await supabase
        .from('webhook_queue')
        .update({ 
          status: 'pending',
          next_retry_at: null,
          retry_count: 0
        })
        .in('status', ['failed', 'processing']);
      
      if (error) throw error;
      
      toast({
        title: 'Reset Successful',
        description: 'Failed webhooks have been reset to pending status',
      });
      
      refetch();
    } catch (error: any) {
      toast({
        title: 'Reset Failed',
        description: error.message || 'Failed to reset webhooks',
        variant: 'destructive',
      });
    }
  };
  
  const getCronStatusIndicator = () => {
    if (isLoadingCron) return <span className="text-muted-foreground">Checking status...</span>;
    
    if (!cronStatus) {
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <AlertCircle size={16} />
          <span>No cron job logs found yet (new cron job will run soon)</span>
        </div>
      );
    }
    
    const isRecent = new Date(cronStatus.execution_time).getTime() > Date.now() - 2 * 60 * 1000;
    
    return (
      <div className={`flex items-center gap-2 ${cronStatus.status === 'success' && isRecent ? 'text-green-600' : 'text-amber-600'}`}>
        {cronStatus.status === 'success' && isRecent ? (
          <>
            <CheckCircle size={16} />
            <span>Auto-processing active (last run: {formatDistanceToNow(new Date(cronStatus.execution_time), { addSuffix: true })})</span>
          </>
        ) : (
          <>
            <AlertCircle size={16} />
            <span>Auto-processing {isRecent ? 'has errors' : 'inactive'} (last attempt: {cronStatus.execution_time ? formatDistanceToNow(new Date(cronStatus.execution_time), { addSuffix: true }) : 'unknown'})</span>
            {cronStatus.message && (
              <span className="ml-1 text-xs">({cronStatus.message})</span>
            )}
          </>
        )}
      </div>
    );
  };
  
  const pendingCount = queueItems?.filter(item => item.status === 'pending').length || 0;
  const failedCount = queueItems?.filter(item => item.status === 'failed').length || 0;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>
            Webhook Queue 
            {pendingCount > 0 && <Badge variant="default" className="ml-2 bg-yellow-500">{pendingCount} pending</Badge>}
            {failedCount > 0 && <Badge variant="destructive" className="ml-2">{failedCount} failed</Badge>}
          </CardTitle>
          <div className="mt-2 text-sm">
            {getCronStatusIndicator()}
          </div>
          <div className="mt-1 text-xs text-blue-600">
            <PlayCircle size={14} className="inline mr-1" />
            <span>Cron job is configured to run every minute</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </Button>
          {failedCount > 0 && (
            <Button
              variant="outline"
              onClick={handleResetFailedWebhooks}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Reset Failed
            </Button>
          )}
          <Button 
            onClick={handleProcessQueue} 
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Process Queue Now'}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {lastProcessedTime && (
          <div className="mb-4 text-sm text-muted-foreground">
            Last manual processing: {formatDistanceToNow(lastProcessedTime, { addSuffix: true })}
          </div>
        )}
        
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ScrollArea className="h-[60vh] w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Queue ID</TableHead>
                  <TableHead>Event Type</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Next Retry</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queueItems?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                      No webhooks in queue
                    </TableCell>
                  </TableRow>
                ) : (
                  queueItems?.map((item) => (
                    <TableRow key={item.queue_id} className={item.status === 'pending' ? 'bg-yellow-50' : item.status === 'failed' ? 'bg-red-50' : ''}>
                      <TableCell className="font-medium">{item.queue_id}</TableCell>
                      <TableCell>
                        {item.webhook_triggers?.webhook_event_types?.event_code || 'Unknown'}
                      </TableCell>
                      <TableCell className="max-w-md truncate">
                        {item.webhook_triggers?.webhook_endpoints?.url || 'Unknown'}
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell>
                        {item.created_at ? 
                          formatDistanceToNow(new Date(item.created_at), { addSuffix: true }) : 
                          'Unknown'
                        }
                      </TableCell>
                      <TableCell>
                        {item.next_retry_at ? 
                          formatDistanceToNow(new Date(item.next_retry_at), { addSuffix: true }) : 
                          '-'
                        }
                      </TableCell>
                      <TableCell className="max-w-md truncate">
                        {item.last_error || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
